import { LegacyStack, Tag, Autocomplete as _Autocomplete } from "@shopify/polaris";
import { OptionDescriptor } from "@shopify/polaris/build/ts/src/types";
import { useState, useCallback, useMemo, useEffect } from "react";

function AutoComplete({
    optionsArray,
    label,
    labelHidden = false,
    selected,
    placeholder,
    onChange,
    listLabel,
    disabled = false,
}: {
    optionsArray: OptionDescriptor[];
    label?: string;
    labelHidden?: boolean;
    selected: string[];
    placeholder: string;
    onChange: (value: string[]) => any;
    listLabel: string;
    disabled?: boolean;
}) {
    const deselectedOptions = useMemo(() => optionsArray, []);
    const [selectedOptions, setSelectedOptions] = useState<string[]>(selected);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState(deselectedOptions);

    const updateText = useCallback(
        (value: string) => {
            if (disabled) return;
            setInputValue(value);

            if (value === "") {
                setOptions(deselectedOptions);
                return;
            }

            const filterRegex = new RegExp(value, "i");
            const resultOptions = deselectedOptions.filter(
                (option) =>
                    (typeof option.label == "string" && option.label.match(filterRegex)) ||
                    option.value.match(filterRegex),
            );

            setOptions(resultOptions);
        },
        [deselectedOptions, disabled],
    );

    const removeTag = useCallback(
        (tag: string) => () => {
            if (disabled) return;
            const options = [...selectedOptions];
            options.splice(options.indexOf(tag), 1);
            setSelectedOptions(options);
        },
        [selectedOptions, disabled],
    );

    useEffect(() => {
        onChange(selectedOptions);
    }, [selectedOptions]);

    const verticalContentMarkup =
        selectedOptions.length > 0 ? (
            <LegacyStack spacing="extraTight" alignment="center">
                {selected.map((_option) => {
                    return (
                        <Tag key={`option${_option}`} onRemove={disabled ? undefined : removeTag(_option)}>
                            {optionsArray.find((option) => option?.value == _option)?.label}
                        </Tag>
                    );
                })}
            </LegacyStack>
        ) : null;

    const textField = (
        <_Autocomplete.TextField
            onChange={updateText}
            label={label}
            labelHidden={labelHidden}
            value={inputValue}
            placeholder={placeholder}
            verticalContent={verticalContentMarkup}
            autoComplete="off"
            disabled={disabled}
        />
    );

    return (
        <div style={{ width: "100%" }}>
            <_Autocomplete
                allowMultiple
                options={options}
                selected={selectedOptions}
                textField={textField}
                onSelect={disabled ? () => {} : setSelectedOptions}
                listTitle={listLabel}
            />
        </div>
    );
}

export default AutoComplete;
